import type { ReportingQueryParams } from "@/schemas";

import { type RawDataQueryParams, type TrimQueryParams } from "../../api/reporting-api";

import { type MachineSelectionQueryParams } from "./use-machine-selection";

const serializeArray = (arr: string[]): string => arr.slice().sort().join(",");

export const REPORTING_QUERY_KEYS = {
	trim: (params: TrimQueryParams, zodAssert = false) =>
		[
			"reporting",
			"trim",
			serializeArray(params.sites),
			serializeArray(params.machinesSelected),
			params.dateRange[0]?.toISO(),
			params.dateRange[1]?.toISO(),
			params.excludeBuiltInWasteInTrimCalculation,
			params.schedules.length,
			...(zodAssert ? [true] : []),
		] as const,
	materialUsage: (params: ReportingQueryParams, zodAssert = false) =>
		[
			"reporting",
			"material-usage",
			serializeArray(params.sites),
			serializeArray(params.machinesSelected),
			params.dateRange[0]?.toISO(),
			params.dateRange[1]?.toISO(),
			params.schedules.length,
			...(zodAssert ? [true] : []),
		] as const,
	cartonProduction: (params: ReportingQueryParams, zodAssert = false) =>
		[
			"reporting",
			"carton-production",
			serializeArray(params.sites),
			serializeArray(params.machinesSelected),
			params.dateRange[0]?.toISO(),
			params.dateRange[1]?.toISO(),
			params.schedules.length,
			...(zodAssert ? [true] : []),
		] as const,
	cartonClosing: (params: ReportingQueryParams) =>
		[
			"reporting",
			"carton-closing",
			serializeArray(params.sites),
			serializeArray(params.machinesSelected),
			params.dateRange[0]?.toISO(),
			params.dateRange[1]?.toISO(),
			params.schedules.length,
		] as const,
	hasCartonClosingBuckets: () => ["reporting", "has-carton-closing-buckets"] as const,
	scheduledReports: {
		all: ["scheduled-reports"] as const,
		detail: (id: string) => [...REPORTING_QUERY_KEYS.scheduledReports.all, id] as const,
		history: (id: string) => [...REPORTING_QUERY_KEYS.scheduledReports.detail(id), "history"] as const,
	},
	errorsAvailability: (params: ReportingQueryParams, zodAssert = false) =>
		[
			"reporting",
			"errors-availability",
			serializeArray(params.sites),
			serializeArray(params.machinesSelected),
			params.dateRange[0]?.toISO(),
			params.dateRange[1]?.toISO(),
			params.schedules.length,
			...(zodAssert ? [true] : []),
		] as const,
	stopReasons: (params: ReportingQueryParams, zodAssert = false) =>
		[
			"reporting",
			"stop-reasons",
			serializeArray(params.sites),
			serializeArray(params.machinesSelected),
			params.dateRange[0]?.toISO(),
			params.dateRange[1]?.toISO(),
			params.schedules.length,
			...(zodAssert ? [true] : []),
		] as const,
	failureReasons: (params: ReportingQueryParams, zodAssert = false) =>
		[
			"reporting",
			"failure-reasons",
			serializeArray(params.sites),
			serializeArray(params.machinesSelected),
			params.dateRange[0]?.toISO(),
			params.dateRange[1]?.toISO(),
			params.schedules.length,
			...(zodAssert ? [true] : []),
		] as const,
	oee: (params: ReportingQueryParams, zodAssert = false) =>
		[
			"reporting",
			"oee",
			serializeArray(params.sites),
			serializeArray(params.machinesSelected),
			params.dateRange[0]?.toISO(),
			params.dateRange[1]?.toISO(),

			params.schedules.length,
			...(zodAssert ? [true] : []),
		] as const,
	rawData: (params: RawDataQueryParams) =>
		[
			"reporting",
			"raw-data",
			serializeArray(params.sites),
			serializeArray(params.machinesSelected),
			params.dateRange[0]?.toISO(),
			params.dateRange[1]?.toISO(),
			params.limit,
			params.excludeBuiltInWasteInTrimCalculation,
			params.schedules.length,
		] as const,
	machineSelection: (params: MachineSelectionQueryParams) =>
		[
			"machine-selection",
			params.schedules
				.map((s) => s.id)
				.sort()
				.join(","),
			serializeArray(params.sites),
			params.startDate.toISO(),
			params.endDate.toISO(),
			params.timeZoneId,
		] as const,
	closingMachineSelection: (params: MachineSelectionQueryParams) =>
		[
			"closing-machine-selection",
			params.schedules
				.map((s) => s.id)
				.sort()
				.join(","),
			serializeArray(params.sites),
			params.startDate.toISO(),
			params.endDate.toISO(),
			params.timeZoneId,
		] as const,
} as const;
